import React, { useState } from "react";
import {
  Modal, Box, Typography, TextField, Button, CircularProgress,
  InputAdornment, Fade,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import RestoreIcon from "@mui/icons-material/Restore";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import axios from "axios";
import { toast } from "react-toastify";

const BASE = "https://api.medstown.com";

const MODES = [
  { id: "adjust", label: "Adjust ±",  icon: <TuneIcon sx={{ fontSize: 18 }} />,      desc: "Add or subtract from current price" },
  { id: "fixed",  label: "Set Fixed", icon: <PriceChangeIcon sx={{ fontSize: 18 }} />, desc: "Set an exact selling price" },
  { id: "reset",  label: "Reset",     icon: <RestoreIcon sx={{ fontSize: 18 }} />,     desc: "Restore to original pricing" },
];

const GRADIENT = {
  adjust: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
  fixed:  "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)",
  reset:  "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
};

const SHADOW = {
  adjust: "rgba(99, 102, 241, 0.35)",
  fixed:  "rgba(37, 99, 235, 0.35)",
  reset:  "rgba(239, 68, 68, 0.35)",
};

/**
 * BulkPriceModal
 * Props:
 *   open, onClose, selectedIds, type ("test" | "package"), onSaved
 */
export default function BulkPriceModal({ open, onClose, selectedIds, type, onSaved }) {
  const [mode, setMode]     = useState("adjust");
  const [val, setVal]       = useState("");
  const [saving, setSaving] = useState(false);

  const adj      = parseFloat(val);
  const hasVal   = val.trim() !== "" && !isNaN(adj);
  const isPos    = hasVal && adj > 0;
  const isNeg    = hasVal && adj < 0;
  const absAdj   = hasVal ? Math.abs(adj) : 0;

  const idKey  = type === "test" ? "testIds" : "packageIds";
  const entity = type === "test" ? "test" : "package";
  const count  = selectedIds.length;

  function reset() { setVal(""); setMode("adjust"); }

  async function handleApply() {
    if (mode === "reset") {
      setSaving(true);
      try {
        await axios.post(`${BASE}/admin/lab/${entity}s/bulk-reset`, { [idKey]: selectedIds });
        toast.success(`Reset ${count} ${entity}(s) to original price`);
        onSaved(); onClose(); reset();
      } catch { toast.error("Reset failed"); }
      finally { setSaving(false); }
      return;
    }

    if (!hasVal) { toast.error("Enter a value"); return; }

    setSaving(true);
    try {
      if (mode === "adjust") {
        await axios.post(`${BASE}/admin/lab/${entity}s/bulk-adjust`, { [idKey]: selectedIds, adjustment: adj });
        toast.success(
          adj > 0
            ? `Added ₹${adj} to ${count} ${entity}(s)`
            : `Reduced ₹${absAdj} from ${count} ${entity}(s)`
        );
      } else {
        await axios.post(`${BASE}/admin/lab/${entity}s/bulk-price`, { [idKey]: selectedIds, medstownPrice: adj });
        toast.success(`Set ₹${adj} for ${count} ${entity}(s)`);
      }
      onSaved(); onClose(); reset();
    } catch { toast.error("Update failed"); }
    finally { setSaving(false); }
  }

  return (
    <Modal open={open} onClose={() => { onClose(); reset(); }} closeAfterTransition>
      <Fade in={open}>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: 420, borderRadius: 4,
          bgcolor: "#fff",
          boxShadow: `0 32px 80px ${SHADOW[mode]}`,
          overflow: "hidden",
          outline: "none",
        }}>

          {/* ── Gradient header ── */}
          <Box sx={{
            background: GRADIENT[mode],
            p: "20px 24px 16px",
            transition: "background 0.3s ease",
          }}>
            <Typography variant="h6" fontWeight={800} color="#fff" letterSpacing={0.3}>
              Bulk Price Update
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <Box sx={{
                bgcolor: "rgba(255,255,255,0.25)", borderRadius: "20px",
                px: 1.5, py: 0.3, display: "inline-flex", alignItems: "center", gap: 0.5,
              }}>
                <Typography variant="caption" color="#fff" fontWeight={700}>
                  {count} {entity}{count !== 1 ? "s" : ""} selected
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ p: "20px 24px 24px" }}>

            {/* ── Mode selector ── */}
            <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
              {MODES.map(m => (
                <Box
                  key={m.id}
                  onClick={() => { setMode(m.id); setVal(""); }}
                  sx={{
                    flex: 1, display: "flex", flexDirection: "column",
                    alignItems: "center", gap: 0.5, py: 1.2, px: 1,
                    borderRadius: 2.5, cursor: "pointer", border: "2px solid",
                    transition: "all 0.2s ease",
                    borderColor: mode === m.id ? "transparent" : "#e2e8f0",
                    background: mode === m.id ? GRADIENT[m.id] : "#f8fafc",
                    boxShadow: mode === m.id ? `0 8px 24px ${SHADOW[m.id]}` : "none",
                    transform: mode === m.id ? "translateY(-2px)" : "none",
                    "&:hover": { transform: "translateY(-2px)", borderColor: "transparent", background: GRADIENT[m.id], boxShadow: `0 8px 24px ${SHADOW[m.id]}` },
                  }}
                >
                  <Box sx={{ color: mode === m.id ? "#fff" : "#64748b", display: "flex" }}>{m.icon}</Box>
                  <Typography variant="caption" fontWeight={700} color={mode === m.id ? "#fff" : "#475569"}>
                    {m.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="body2" color="text.secondary" mb={2}>
              {MODES.find(m => m.id === mode)?.desc}
            </Typography>

            {/* ── Input area ── */}
            {mode !== "reset" && (
              <>
                {mode === "adjust" && (
                  <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setVal(v => v && !isNaN(parseFloat(v)) ? String(Math.abs(parseFloat(v))) : v)}
                      sx={{ minWidth: 40, borderColor: "#10b981", color: "#10b981", "&:hover": { bgcolor: "#f0fdf4", borderColor: "#10b981" } }}
                      startIcon={<AddIcon />}
                    >Increase</Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setVal(v => v && !isNaN(parseFloat(v)) ? String(-Math.abs(parseFloat(v))) : v)}
                      sx={{ minWidth: 40, borderColor: "#ef4444", color: "#ef4444", "&:hover": { bgcolor: "#fef2f2", borderColor: "#ef4444" } }}
                      startIcon={<RemoveIcon />}
                    >Decrease</Button>
                  </Box>
                )}

                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={mode === "adjust" ? "Amount to add / subtract (₹)" : "Fixed Selling Price (₹)"}
                  placeholder={mode === "adjust" ? "e.g. +50 or -20" : "e.g. 299"}
                  value={val}
                  onChange={e => setVal(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography fontWeight={700} color={isPos ? "#10b981" : isNeg ? "#ef4444" : "#94a3b8"}>₹</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: isPos ? "#10b981" : isNeg ? "#ef4444" : undefined,
                        borderWidth: hasVal ? 2 : 1,
                      },
                    },
                  }}
                />

                {/* ── Live preview pill ── */}
                {hasVal && mode === "adjust" && (
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    p: "10px 14px", borderRadius: 2.5, mb: 2,
                    background: isPos ? "linear-gradient(135deg, #f0fdf4, #dcfce7)" : "linear-gradient(135deg, #fef2f2, #fee2e2)",
                    border: `1.5px solid ${isPos ? "#86efac" : "#fca5a5"}`,
                  }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: "50%",
                      bgcolor: isPos ? "#10b981" : "#ef4444",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      {isPos ? <AddIcon sx={{ fontSize: 18, color: "#fff" }} /> : <RemoveIcon sx={{ fontSize: 18, color: "#fff" }} />}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={700} color={isPos ? "#15803d" : "#b91c1c"}>
                        {isPos ? `+₹${absAdj}` : `-₹${absAdj}`} per item
                      </Typography>
                      <Typography variant="caption" color={isPos ? "#16a34a" : "#dc2626"}>
                        Applied to each item's current selling price
                      </Typography>
                    </Box>
                  </Box>
                )}

                {hasVal && mode === "fixed" && (
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    p: "10px 14px", borderRadius: 2.5, mb: 2,
                    background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
                    border: "1.5px solid #93c5fd",
                  }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: "50%",
                      bgcolor: "#2563eb",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <PriceChangeIcon sx={{ fontSize: 18, color: "#fff" }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={700} color="#1d4ed8">
                        ₹{adj} for all {count} items
                      </Typography>
                      <Typography variant="caption" color="#3b82f6">
                        Overrides individual prices
                      </Typography>
                    </Box>
                  </Box>
                )}
              </>
            )}

            {/* ── Reset confirmation ── */}
            {mode === "reset" && (
              <Box sx={{
                p: "14px 16px", borderRadius: 2.5, mb: 2,
                background: "linear-gradient(135deg, #fffbeb, #fef3c7)",
                border: "1.5px solid #fcd34d",
              }}>
                <Typography variant="body2" fontWeight={700} color="#92400e" mb={0.5}>
                  Restore original pricing
                </Typography>
                <Typography variant="caption" color="#a16207">
                  Tests → MRP &nbsp;|&nbsp; Packages → Net Rate (OH Cost)
                </Typography>
              </Box>
            )}

            {/* ── Action buttons ── */}
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => { onClose(); reset(); }}
                disabled={saving}
                sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b" }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApply}
                disabled={saving || (mode !== "reset" && !hasVal)}
                sx={{
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontWeight: 700,
                  background: GRADIENT[mode],
                  boxShadow: `0 8px 20px ${SHADOW[mode]}`,
                  "&:hover": { background: GRADIENT[mode], opacity: 0.92 },
                  "&:disabled": { opacity: 0.5 },
                }}
              >
                {saving
                  ? <CircularProgress size={18} sx={{ color: "#fff" }} />
                  : mode === "reset"
                  ? `Reset ${count} item${count !== 1 ? "s" : ""}`
                  : mode === "adjust"
                  ? `Apply to ${count} item${count !== 1 ? "s" : ""}`
                  : `Set Price for ${count} item${count !== 1 ? "s" : ""}`
                }
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}
